import React, { useState, useEffect } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";

import { useCheckpoints, useEpicThroughId } from "../../../reactQuery/epics";
import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { isAdmin } from "../../../utils/constants";
import { useResponsive } from "../../../hooks/useResponsive";
import useToastPromise from "../../../hooks/useToast";
import { useMilestoneMutations } from "./hooks/useMilestoneMutations";
import { useMilestoneHandlers } from "./hooks/useMilestoneHandlers";
import MilestoneHistory from "../milestoneHistory";
import FormDialog from "../../Dialog/FormDialog";
import AddCheckpoint from "../addCheckpoint";
import MilestoneHeader from "./components/MilestoneHeader";
import MilestoneDescription from "./components/MilestoneDescription";
import MilestoneProperties from "./components/MilestoneProperties";
import MilestoneCheckpoints from "./components/MilestoneCheckpoints";
import MilestoneActions from "./components/MilestoneActions";
import useBoardIdByKey from "../../../hooks/useBoardIdByKey";

export default function EditMilestone({ open, handleClose, title, boardName }) {
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [targetDate, setTargetDate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [addCheckpoint, setCheckpoint] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { id } = useBoardIdByKey();
  const [milestone] = useCustomSearchParams("milestone");
  const { epics } = useEpicThroughId(milestone);
  const { checkpoint } = useCheckpoints(milestone);
  const isMobile = useResponsive("down", "md");
  const { showToastPromise } = useToastPromise();
  const isEditable = isAdmin() || boardName?.data?.responseData?.is_owner || boardName?.data?.responseData?.board_permission?.is_admin;

  const { updateCardMutate, checkpointMutate, deleteMutate } = useMilestoneMutations({
    milestone,
    id,
    setShowEditor,
    handleClose,
    setSearchParams,
    searchParams,
    showToastPromise
  });

  const {
    handleDescription,
    handleStartdate,
    handleTargetdate,
    handleCheckBox,
    handleDelete,
    handleComplete,
    handleCheckName,
    handleNameChange,
    handleNameKeyDown,
  } = useMilestoneHandlers({
    milestone,
    id,
    description,
    name,
    error,
    epics,
    updateCardMutate,
    checkpointMutate,
    deleteMutate,
    setName,
    setErrorText,
    setError,
  });

  useEffect(() => {
    if (epics) {
      const epicData = epics?.data?.data?.responseData.epic;
      setDescription(epicData.description);
      setName(epicData.epic_name);
      setStartDate(
        epicData.start_date
          ? dayjs(moment(epicData.start_date).format("MM/DD/YYYY"))
          : null
      );
      setTargetDate(
        epicData.target_release_date
          ? dayjs(moment(epicData.target_release_date).format("MM/DD/YYYY"))
          : null
      );
    }
  }, [epics]);

  const titleComp = (
    <MilestoneHeader
      name={name}
      setName={handleNameChange}
      error={error}
      errorText={errorText}
      handleNameKeyDown={handleNameKeyDown}
      handleDelete={handleDelete}
      isEditable={isEditable}
    />
  );

  return (
    <React.Fragment>
      <FormDialog
        open={open}
        handleClose={handleClose}
        title={titleComp}
        width={1180}
        className="edit-milestone-dialog"
        fullWidth={true}
      >
        <Stack direction="row" justifyContent="space-between">
          <Box sx={{ flexGrow: 1, width: isMobile ? "100%" : "" }}>
            <Grid container spacing={2}>
              <Grid item xs={isMobile ? 12 : 8}>
                <MilestoneDescription
                  description={description}
                  setDescription={setDescription}
                  handleDescription={handleDescription}
                  setShowEditor={setShowEditor}
                />
                {!isMobile && <MilestoneHistory />}
              </Grid>

              <Grid item xs={isMobile ? 12 : 4} mt={isMobile ? 0 : 2.4}>
                {isMobile && (
                  <Typography fontSize="16px" fontWeight={700}>
                    PROPERTIES
                  </Typography>
                )}

                <MilestoneProperties
                  startDate={startDate}
                  targetDate={targetDate}
                  handleStartdate={handleStartdate}
                  handleTargetdate={handleTargetdate}
                  epicData={epics?.data?.data.responseData}
                  isEditable={isEditable}
                >
                  <MilestoneCheckpoints
                    checkpoint={checkpoint}
                    handleCheckBox={handleCheckBox}
                    handleCheckName={handleCheckName}
                    setCheckpoint={setCheckpoint}
                    isEditable={isEditable}
                  />
                  <MilestoneActions
                    epicData={epics?.data?.data.responseData.epic}
                    handleComplete={handleComplete}
                    isEditable={isEditable}
                  />
                </MilestoneProperties>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </FormDialog>

      <AddCheckpoint
        open={addCheckpoint}
        handleClose={() => setCheckpoint(false)}
      />
    </React.Fragment>
  );
}
